/**
 * MyWalletPage.js — One page that adapts based on role.
 *
 * Used at /wallet-earnings (gestor), /admin/wallet-ceo (admin),
 * and /provider/wallet (proveedor CU).
 *
 * Shows: balance UYU + tabla de movimientos with operation link.
 */
import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Wallet, TrendingUp, ArrowDownCircle, ArrowUpCircle, Send, Coins, Activity } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { extractErrorMessage } from '@/utils/errors';
import WalletBalanceConverter from '@/components/WalletBalanceConverter';
import PremiumStatCard from '@/components/premium/PremiumStatCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ENTRY_LABEL = {
  profit_share:       { label: 'Ganancia compartida',  color: 'text-emerald-600', icon: TrendingUp },
  manual_adjustment:  { label: 'Ajuste manual',        color: 'text-blue-600',    icon: Wallet },
  withdrawal:         { label: 'Retiro',               color: 'text-rose-600',    icon: ArrowDownCircle },
  recharge_inflow:    { label: 'Recarga',              color: 'text-emerald-600', icon: ArrowUpCircle },
  remittance_outflow: { label: 'Envío remesa',         color: 'text-rose-600',    icon: ArrowDownCircle },
};

const ROLE_TITLES = {
  gestor:        { title: 'Mi Wallet de Ganancias',     subtitle: 'Acumula tu ganancia por cada remesa entregada.' },
  admin:         { title: 'Wallet CEO',                 subtitle: 'Saldo proveniente de rendiciones confirmadas.' },
  payment_agent: { title: 'Mi Wallet (Proveedor CU)',   subtitle: 'Acumula tu 50% de cada remesa entregada.' },
};

const MyWalletPage = () => {
  const { getAuthHeaders, user } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [moves, setMoves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [withdrawals, setWithdrawals] = useState([]);
  const [withdrawOpen, setWithdrawOpen] = useState(false);
  const [withdrawForm, setWithdrawForm] = useState({ amount: '', account_info: '', note: '' });
  const [submittingWithdraw, setSubmittingWithdraw] = useState(false);
  const [rateInfo, setRateInfo] = useState(null);  // last deposit rate
  const [currency, setCurrency] = useState('UYU'); // 'UYU' | 'CONV'

  // CEO wallet doesn't allow self-withdrawals (no need — CEO IS the approver).
  const canWithdraw = user?.role === 'gestor' || user?.role === 'payment_agent';

  // The "other" currency the wallet can display in. The CEO and the
  // Cuban provider trade in real USDT; the gestor only sees the masked
  // alias "USD" (we never display the word USDT to gestores).
  const convLabel = user?.role === 'gestor' ? 'USD' : 'USDT';

  const load = async () => {
    try {
      const requests = [
        axios.get(`${API}/wallets/me`, { headers: getAuthHeaders() }),
        axios.get(`${API}/wallets/me/movements?limit=200`, { headers: getAuthHeaders() }),
        axios.get(`${API}/wallets/last-deposit-rate`, { headers: getAuthHeaders() }),
      ];
      if (canWithdraw) {
        requests.push(axios.get(`${API}/withdrawals/me`, { headers: getAuthHeaders() }));
      }
      const [w, m, r, wr] = await Promise.all(requests);
      setWallet(w.data);
      setMoves(m.data.items || []);
      setRateInfo(r.data || null);
      if (wr) setWithdrawals(wr.data.items || []);
    } catch (err) {
      // Wallet aún no creada, o red caída — log defensivo y mostrar
      // estado vacío. Nunca romper la vista.
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[MyWalletPage] load failed:', err?.message || err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!user) return;
    load();
  }, [getAuthHeaders, user]);

  // v4: every movement now carries `amount_usdt` and the wallet exposes
  // `balance_usdt` as its authoritative balance. The toggle becomes a
  // pure display switch — no conversion math needed at render time except
  // when the v3 fields are stale (legacy fallback).
  const rate = rateInfo?.rate_uyu_usdt || 0;
  const canConvert = rate > 0;
  const currencyLabel = currency === 'UYU' ? 'UYU' : convLabel;

  // Pick the wallet's balance in the currently selected currency. We prefer
  // the v4 stored value (balance_usdt) over a UYU/rate computation.
  const balanceInSelected = useMemo(() => {
    if (!wallet) return null;
    if (currency === 'UYU') return Number(wallet.balance_uyu || 0);
    // CONV (USDT or USD alias). v4: trust balance_usdt if present.
    if (wallet.balance_usdt != null) return Number(wallet.balance_usdt);
    return canConvert ? Number(wallet.balance_uyu || 0) / rate : null;
  }, [wallet, currency, rate, canConvert]);

  const submitWithdraw = async (e) => {
    e.preventDefault();
    const amt = parseFloat(withdrawForm.amount);
    if (!amt || amt <= 0) return;
    setSubmittingWithdraw(true);
    try {
      await axios.post(`${API}/withdrawals`, {
        amount: amt,
        currency: 'UYU',
        account_info: withdrawForm.account_info,
        note: withdrawForm.note,
      }, { headers: getAuthHeaders() });
      toast.success('Solicitud de retiro enviada al CEO.');
      setWithdrawOpen(false);
      setWithdrawForm({ amount: '', account_info: '', note: '' });
      load();
    } catch (err) {
      toast.error(extractErrorMessage(err, 'No se pudo solicitar el retiro'));
    } finally {
      setSubmittingWithdraw(false);
    }
  };

  const meta = ROLE_TITLES[user?.role] || { title: 'Wallet', subtitle: '' };
  const fmt = (n) => Number(n || 0).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const inflowCount = moves.filter((m) => Number(m.amount || 0) > 0).length;
  const outflowCount = moves.filter((m) => Number(m.amount || 0) < 0).length;

  return (
    <div className="space-y-6" data-testid="my-wallet-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <Wallet className="w-6 h-6 text-emerald-600" />
          {meta.title}
          <span className="ml-2 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 font-bold">
            {user?.role === 'admin' ? 'CEO' : user?.role === 'payment_agent' ? 'Proveedor' : 'Gestor'}
          </span>
        </h1>
        <p className="text-xs text-slate-500 mt-1">{meta.subtitle}</p>
      </div>

      {/* Iter 64 · Row de KPIs premium (light-mode fintech) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <PremiumStatCard
          label="Saldo UYU"
          value={loading ? '…' : fmt(wallet?.balance_uyu)}
          sublabel="Pesos uruguayos"
          icon={Wallet}
          tone="emerald"
          loading={loading}
          testId="stat-balance-uyu"
        />
        <PremiumStatCard
          label={`Saldo ${convLabel}`}
          value={loading ? '…' : fmt(wallet?.balance_usdt || wallet?.balance_usd)}
          sublabel={user?.role === 'gestor' ? 'Dólar digital' : 'Tether USDT'}
          icon={Coins}
          tone="teal"
          loading={loading}
          testId="stat-balance-usdt"
        />
        <PremiumStatCard
          label="Ingresos"
          value={loading ? '…' : inflowCount}
          sublabel="Movimientos entrantes"
          icon={ArrowUpCircle}
          tone="sky"
          loading={loading}
          testId="stat-inflow-count"
        />
        <PremiumStatCard
          label="Egresos"
          value={loading ? '…' : outflowCount}
          sublabel="Retiros y salidas"
          icon={ArrowDownCircle}
          tone="rose"
          loading={loading}
          testId="stat-outflow-count"
        />
      </div>

      {/* Iter 63 · Conversor UYU ↔ USDT/USD (no muta balances reales) */}
      {!loading && wallet && (
        <WalletBalanceConverter
          balanceUyu={Number(wallet.balance_uyu || 0)}
          balanceUsdt={Number(wallet.balance_usdt || wallet.balance_usd || 0)}
          role={user?.role}
          label={`Saldo total · ${meta.title}`}
          testid="my-wallet-converter"
        />
      )}

      <Card className="p-6 border-0 shadow-sm bg-gradient-to-br from-emerald-500 to-emerald-700 text-white" data-testid="wallet-balance-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-emerald-100 text-xs uppercase tracking-wide">Saldo disponible ({currencyLabel})</p>
            <p className="text-4xl font-bold font-mono mt-1" data-testid="wallet-balance-amount">
              {loading
                ? '…'
                : balanceInSelected == null
                  ? '—'
                  : Number(balanceInSelected).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
            </p>
            {Number(wallet?.balance_usd || 0) > 0 && currency === 'UYU' && (
              <p className="text-emerald-100 text-xs mt-2 font-mono">
                + {Number(wallet.balance_usd).toLocaleString('es-ES', { minimumFractionDigits: 2 })} USD
              </p>
            )}
            {/* Currency toggle */}
            <div className="mt-4 inline-flex items-center bg-white/15 backdrop-blur rounded-lg p-1" data-testid="wallet-currency-toggle">
              <button
                type="button"
                onClick={() => setCurrency('UYU')}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition ${currency === 'UYU' ? 'bg-white text-emerald-700' : 'text-emerald-50'}`}
                data-testid="toggle-currency-uyu"
              >
                UYU
              </button>
              <button
                type="button"
                onClick={() => setCurrency('CONV')}
                disabled={!canConvert}
                className={`px-3 py-1 rounded-md text-xs font-semibold transition ${currency === 'CONV' ? 'bg-white text-emerald-700' : 'text-emerald-50'} ${!canConvert ? 'opacity-40 cursor-not-allowed' : ''}`}
                title={canConvert ? '' : 'Aún no hay tasa registrada del último depósito'}
                data-testid="toggle-currency-conv"
              >
                {convLabel}
              </button>
            </div>
            {/* Rate info row */}
            {canConvert && (
              <p className="text-[10px] text-emerald-50/90 mt-2 font-mono" data-testid="wallet-rate-info">
                Tasa: 1 {convLabel} = {Number(rate).toLocaleString('es-ES', { minimumFractionDigits: 2 })} UYU
                {rateInfo?.source === 'settlement' && rateInfo?.amount_uyu && rateInfo?.usdt_received
                  ? ` · último dep. ${Number(rateInfo.amount_uyu).toLocaleString('es-ES', { maximumFractionDigits: 0 })} UYU ↔ ${Number(rateInfo.usdt_received).toLocaleString('es-ES', { maximumFractionDigits: 2 })} ${convLabel}`
                  : ''}
              </p>
            )}
            {!canConvert && (
              <p className="text-[10px] text-emerald-50/80 mt-2">Sin tasa de depósito todavía — solo UYU disponible.</p>
            )}
          </div>
          {canWithdraw && Number(wallet?.balance_uyu || 0) > 0 && (
            <Button
              onClick={() => setWithdrawOpen(true)}
              className="bg-white text-emerald-700 hover:bg-emerald-50"
              data-testid="withdraw-btn"
            >
              <Send className="w-4 h-4 mr-2" /> Solicitar retiro
            </Button>
          )}
        </div>
      </Card>

      {/* Pending withdrawal requests */}
      {canWithdraw && withdrawals.length > 0 && (
        <Card className="p-5 border-0 shadow-sm" data-testid="withdrawals-list">
          <h3 className="font-bold text-slate-900 mb-3">Mis solicitudes de retiro</h3>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Monto</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Cuenta</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {withdrawals.map((w) => (
                <TableRow key={w.id}>
                  <TableCell className="text-xs text-slate-500">{new Date(w.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}</TableCell>
                  <TableCell className="font-mono">{Number(w.amount).toLocaleString('es-ES', { minimumFractionDigits: 2 })} {w.currency}</TableCell>
                  <TableCell>
                    <Badge className={w.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : w.status === 'rejected' ? 'bg-rose-100 text-rose-700' : 'bg-amber-100 text-amber-700'}>
                      {w.status === 'approved' ? 'Aprobado' : w.status === 'rejected' ? 'Rechazado' : 'Pendiente'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 truncate max-w-xs">{w.account_info || '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Card className="p-5 border-0 shadow-sm">
        <h3 className="font-bold text-slate-900 mb-3">Movimientos recientes</h3>
        {moves.length === 0 ? (
          <p className="text-sm text-slate-400 py-8 text-center">Sin movimientos todavía.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Descripción</TableHead>
                <TableHead className="text-right">Monto</TableHead>
                <TableHead className="text-right">Saldo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {moves.map((m) => {
                const cfg = ENTRY_LABEL[m.entry_type] || { label: m.entry_type, color: 'text-slate-700', icon: Wallet };
                const Icon = cfg.icon;
                const isPos = m.amount >= 0;
                const movCurrency = m.currency || 'UYU';
                // v4: prefer the movement's stored amount_usdt + balance_after_usdt.
                // Fall back to dividing UYU by current rate for legacy rows.
                const showConv = currency === 'CONV' && (movCurrency === 'UYU' || movCurrency === 'USD');
                let dispAmount, dispBalance;
                if (showConv) {
                  dispAmount = m.amount_usdt != null
                    ? m.amount_usdt
                    : (canConvert ? m.amount / rate : 0);
                  dispBalance = m.balance_after_usdt != null
                    ? m.balance_after_usdt
                    : (canConvert ? m.balance_after / rate : 0);
                } else if (currency === 'UYU' && movCurrency === 'USDT') {
                  // Reverse direction: a USDT movement displayed in UYU.
                  const r = m.rate_at_time || rate;
                  dispAmount = r > 0 ? m.amount * r : m.amount;
                  dispBalance = r > 0 ? m.balance_after * r : m.balance_after;
                } else {
                  dispAmount = m.amount;
                  dispBalance = m.balance_after;
                }
                const dispLabel = showConv ? convLabel : (currency === 'UYU' ? 'UYU' : movCurrency);
                return (
                  <TableRow key={m.id} data-testid={`wallet-mov-${m.id.slice(0, 8)}`}>
                    <TableCell className="text-xs text-slate-500">
                      {new Date(m.created_at).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' })}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={cfg.color}>
                        <Icon className="w-3 h-3 mr-1" />
                        {cfg.label}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-600 max-w-md truncate" title={m.description}>
                      {m.description || '—'}
                    </TableCell>
                    <TableCell className={`text-right font-mono font-bold ${isPos ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {isPos ? '+' : ''}{Number(dispAmount).toLocaleString('es-ES', { minimumFractionDigits: 2 })} {dispLabel}
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs text-slate-500">
                      {Number(dispBalance).toLocaleString('es-ES', { minimumFractionDigits: 2 })}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {/* Withdraw dialog */}
      <Dialog open={withdrawOpen} onOpenChange={setWithdrawOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Solicitar retiro</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitWithdraw} className="space-y-4">
            <div className="space-y-2">
              <Label>Monto (UYU) *</Label>
              <Input
                type="number"
                min="1"
                step="any"
                max={wallet?.balance_uyu || 0}
                value={withdrawForm.amount}
                onChange={(e) => setWithdrawForm({ ...withdrawForm, amount: e.target.value })}
                required
                data-testid="withdraw-amount-input"
              />
              <p className="text-[10px] text-slate-400">Disponible: {Number(wallet?.balance_uyu || 0).toFixed(2)} UYU</p>
            </div>
            <div className="space-y-2">
              <Label>Cuenta destino *</Label>
              <Input
                value={withdrawForm.account_info}
                onChange={(e) => setWithdrawForm({ ...withdrawForm, account_info: e.target.value })}
                placeholder="IBAN / CBU / cuenta — dónde el CEO debe acreditar"
                required
                data-testid="withdraw-account-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Nota (opcional)</Label>
              <Input
                value={withdrawForm.note}
                onChange={(e) => setWithdrawForm({ ...withdrawForm, note: e.target.value })}
                placeholder="Cualquier comentario para el CEO"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setWithdrawOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={submittingWithdraw} className="bg-emerald-600 hover:bg-emerald-700" data-testid="withdraw-submit-btn">
                {submittingWithdraw ? 'Enviando…' : 'Solicitar retiro'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyWalletPage;
